import axios from 'axios';
import { httpClientConfig } from '@/shared/config/http-client';
import { attachSessionGuard } from '@/shared/lib/auth';

class HttpClient {
  constructor() {
    this.apiClient = axios.create({
      baseURL: httpClientConfig.baseURL,
      timeout: httpClientConfig.timeout,
      headers: httpClientConfig.headers,
    });

    // Токен, повтор зі свіжим токеном і повторний вхід — як у глобального axios
    attachSessionGuard(this.apiClient);
  }

  /**
   * Makes a GET request.
   * @param {string} url - The URL to request.
   * @param {object} [params] - Query parameters.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  get(url, params = {}, config = {}) {
    return this.apiClient.get(url, { params, ...config });
  }

  /**
   * Makes a POST request.
   * @param {string} url - The URL to request.
   * @param {object} [data] - Request body data.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  post(url, data = {}, config = {}) {
    return this.apiClient.post(url, data, config);
  }

  /**
   * Makes a PUT request.
   * @param {string} url - The URL to request.
   * @param {object} [data] - Request body data.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  put(url, data = {}, config = {}) {
    return this.apiClient.put(url, data, config);
  }

  /**
   * Makes a PATCH request.
   * @param {string} url - The URL to request.
   * @param {object} [data] - Request body data.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  patch(url, data = {}, config = {}) {
    return this.apiClient.patch(url, data, config);
  }

  /**
   * Makes a DELETE request.
   * @param {string} url - The URL to request.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  delete(url, config = {}) {
    return this.apiClient.delete(url, config);
  }

  /**
   * Uploads a file using POST request with multipart/form-data.
   * @param {string} url - The URL to request.
   * @param {File|Blob} file - The file to upload.
   * @param {string} [fieldName] - The field name for the file.
   * @param {object} [additionalData] - Additional form data to send with the file.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  uploadFile(url, file, fieldName = 'file', additionalData = {}, config = {}) {
    const formData = new FormData();
    formData.append(fieldName, file);

    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return this.apiClient.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Uploads multiple files using POST request with multipart/form-data.
   * @param {string} url - The URL to request.
   * @param {Array<File|Blob>} files - Array of files to upload.
   * @param {string} [fieldName] - The field name for the files.
   * @param {object} [additionalData] - Additional form data to send with the files.
   * @param {import('axios').AxiosRequestConfig} [config] - Additional Axios configuration.
   * @returns {Promise<import('axios').AxiosResponse>} The Axios response.
   */
  uploadFiles(url, files, fieldName = 'files', additionalData = {}, config = {}) {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`${fieldName}[${index}]`, file);
    });

    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return this.apiClient.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

const httpClient = new HttpClient();
export default httpClient;
